import { z } from "zod";
import { dateOnlySchema, listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { queryBooleanSchema, timestampsShape, userRefSchema } from "./common.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * TIME TRACKING, TIMESHEETS, BUDGETS AND PROFITABILITY (PRD §7 Item 10).
 *
 * Two rules shape every schema here.
 *
 *  1. **An hour is a fact, not an opinion.** A time entry snapshots the bill
 *     rate and the cost rate that were in force on the day it was worked, so a
 *     rate-card change next quarter cannot rewrite last quarter's margin.
 *  2. **Money is masked, not hidden.** A caller without `financials:view` gets
 *     `null` for every `*Minor` field — the entry, the timesheet and the week
 *     grid are still fully usable, they just carry no rates (PRD F3: members
 *     never see cost rates or margins).
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Where the entry came from. `timer` rows are written when a running timer stops. */
export const TIME_ENTRY_SOURCES = ["timer", "manual", "import"] as const;
export type TimeEntrySource = (typeof TIME_ENTRY_SOURCES)[number];
export const timeEntrySourceSchema = z.enum(TIME_ENTRY_SOURCES);

/** Non-billable buckets come straight from US-10.2. `client` is billable work. */
export const TIME_CATEGORIES = ["client", "internal", "pitch", "admin", "learning"] as const;
export type TimeCategory = (typeof TIME_CATEGORIES)[number];
export const timeCategorySchema = z.enum(TIME_CATEGORIES);

/** `invoiced` means an invoice owns this hour; it can only be released by un-billing that document. */
export const TIME_BILLED_STATES = ["unbilled", "invoiced"] as const;
export type TimeBilledState = (typeof TIME_BILLED_STATES)[number];
export const timeBilledStateSchema = z.enum(TIME_BILLED_STATES);

/** Shared by entries, timesheets and expenses — one word means one thing everywhere. */
export const APPROVAL_STATES = ["draft", "submitted", "approved", "rejected"] as const;
export type ApprovalState = (typeof APPROVAL_STATES)[number];
export const approvalStateSchema = z.enum(APPROVAL_STATES);

export const TIMESHEET_STATUSES = ["open", "submitted", "approved", "rejected"] as const;
export type TimesheetStatus = (typeof TIMESHEET_STATUSES)[number];
export const timesheetStatusSchema = z.enum(TIMESHEET_STATUSES);

/**
 * `hours` burns minutes, `money` burns internal COST (time × cost rate +
 * expenses, US-10.4), `retainer` burns billable VALUE (time × bill rate +
 * rebillable expenses) against the retainer fee.
 */
export const BUDGET_TYPES = ["hours", "money", "retainer"] as const;
export type BudgetType = (typeof BUDGET_TYPES)[number];
export const budgetTypeSchema = z.enum(BUDGET_TYPES);

/** Rounding increments offered by US-10.2. */
export const TIME_ROUNDING_INCREMENTS = [1, 5, 6, 15] as const;
export type TimeRoundingIncrement = (typeof TIME_ROUNDING_INCREMENTS)[number];

/** How an unbilled list is collapsed into invoice lines (US-10.7). */
export const UNBILLED_GROUPINGS = ["task", "person", "service", "project"] as const;
export type UnbilledGrouping = (typeof UNBILLED_GROUPINGS)[number];

/** What profitability is sliced by (US-10.6). */
export const PROFITABILITY_DIMENSIONS = ["client", "engagement", "project", "service"] as const;
export type ProfitabilityDimension = (typeof PROFITABILITY_DIMENSIONS)[number];

// ---------------------------------------------------------------------------
// Time entries
// ---------------------------------------------------------------------------

/** One tracked block of work. `*Minor` fields are null for a caller without `financials:view`. */
export const timeEntrySchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  user: userRefSchema.nullable(),
  projectId: uuidSchema.nullable(),
  projectName: z.string().nullable(),
  taskId: uuidSchema.nullable(),
  taskTitle: z.string().nullable(),
  clientId: uuidSchema.nullable(),
  spentOn: dateOnlySchema,
  minutes: z.number().int(),
  description: z.string().nullable(),
  billable: z.boolean(),
  category: timeCategorySchema,
  source: timeEntrySourceSchema,
  billedState: timeBilledStateSchema,
  invoiceId: uuidSchema.nullable(),
  /** The document that owns this hour, e.g. "INV/26-27/0042" — what a 409 names. */
  invoiceNumber: z.string().nullable(),
  billRateMinor: minorSchema.nullable(),
  costRateMinor: minorSchema.nullable(),
  /** minutes ÷ 60 × billRate, rounded to the paisa. Null when masked. */
  billableMinor: minorSchema.nullable(),
  costMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  approvalState: approvalStateSchema,
  approvedByUserId: uuidSchema.nullable(),
  approvedAt: timestampSchema.nullable(),
  timesheetId: uuidSchema.nullable(),
  /** True when the entry may no longer be edited (billed or approved). */
  locked: z.boolean(),
  lockReason: z.string().nullable(),
  version: z.number().int(),
  ...timestampsShape,
});
export type TimeEntryDto = z.infer<typeof timeEntrySchema>;

/** Shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const timeEntryWritableShape = {
  projectId: uuidSchema.nullable(),
  taskId: uuidSchema.nullable(),
  spentOn: dateOnlySchema,
  minutes: z.number().int().min(1).max(1440),
  description: z.string().trim().max(2000).nullable(),
  billable: z.boolean(),
  category: timeCategorySchema,
  userId: uuidSchema,
};

/** POST /api/v1/time/entries (perm `time:track`) → 201 TimeEntry. Someone else's time needs `time:manage`. */
export const createTimeEntryInputSchema = z
  .object({
    ...timeEntryWritableShape,
    projectId: timeEntryWritableShape.projectId.optional(),
    taskId: timeEntryWritableShape.taskId.optional(),
    description: timeEntryWritableShape.description.optional(),
    billable: timeEntryWritableShape.billable.optional(),
    category: timeEntryWritableShape.category.optional(),
    userId: timeEntryWritableShape.userId.optional(),
  })
  .strict();
export type CreateTimeEntryInput = z.input<typeof createTimeEntryInputSchema>;

/**
 * PATCH /api/v1/time/entries/:id (perm `time:track`) → TimeEntry.
 * `version` makes the write conditional: a stale or locked row answers 409 with
 * the reason (`locked by INV/26-27/0042`, `approved`, or `stale`).
 */
export const updateTimeEntryInputSchema = z
  .object({ ...timeEntryWritableShape, version: z.number().int().min(1) })
  .partial()
  .strict();
export type UpdateTimeEntryInput = z.input<typeof updateTimeEntryInputSchema>;

export const listTimeEntriesQuerySchema = listQuerySchema.extend({
  userId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  clientId: uuidSchema.optional(),
  taskId: uuidSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  billable: queryBooleanSchema.optional(),
  billedState: timeBilledStateSchema.optional(),
  approvalState: approvalStateSchema.optional(),
  sort: z.enum(["spentOn", "minutes", "createdAt"]).optional(),
});
export type ListTimeEntriesQuery = z.infer<typeof listTimeEntriesQuerySchema>;

/** GET /api/v1/time/entries → page of entries plus the totals of the WHOLE filter, not the page. */
export const timeEntryPageSchema = z.object({
  items: z.array(timeEntrySchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  totalMinutes: z.number().int(),
  billableMinutes: z.number().int(),
  billableMinor: minorSchema.nullable(),
  costMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
});
export type TimeEntryPage = z.infer<typeof timeEntryPageSchema>;

// ---------------------------------------------------------------------------
// Timers
// ---------------------------------------------------------------------------

/** The one timer a user may have running. `null` from GET /time/timer when nothing is running. */
export const timerSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  projectId: uuidSchema.nullable(),
  projectName: z.string().nullable(),
  taskId: uuidSchema.nullable(),
  taskTitle: z.string().nullable(),
  description: z.string().nullable(),
  startedAt: timestampSchema,
  /** Server-computed, so a reloaded tab shows the same number as the phone. */
  elapsedSeconds: z.number().int(),
  ...timestampsShape,
});
export type TimerDto = z.infer<typeof timerSchema>;

/** GET /api/v1/time/timer (perm `time:track`) → the running timer or null. */
export const currentTimerSchema = z.object({ timer: timerSchema.nullable() });
export type CurrentTimerDto = z.infer<typeof currentTimerSchema>;

/** POST /api/v1/time/timer/start (perm `time:track`) → 201 Timer. A second timer → CONFLICT. */
export const startTimerInputSchema = z
  .object({
    projectId: uuidSchema.nullable().optional(),
    taskId: uuidSchema.nullable().optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    /** Backdate the start (a forgotten timer). Never in the future. */
    startedAt: timestampSchema.optional(),
  })
  .strict();
export type StartTimerInput = z.input<typeof startTimerInputSchema>;

/** POST /api/v1/time/timer/stop (perm `time:track`) → the time entry it wrote. */
export const stopTimerInputSchema = z
  .object({
    description: z.string().trim().max(2000).nullable().optional(),
    billable: z.boolean().optional(),
    /** Overrides the elapsed time (the user corrects it in the stop dialog). */
    minutes: z.number().int().min(1).max(1440).optional(),
  })
  .strict();
export type StopTimerInput = z.input<typeof stopTimerInputSchema>;

// ---------------------------------------------------------------------------
// Timesheets (US-10.3)
// ---------------------------------------------------------------------------

export const timesheetSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  user: userRefSchema.nullable(),
  weekStart: dateOnlySchema,
  weekEnd: dateOnlySchema,
  status: timesheetStatusSchema,
  totalMinutes: z.number().int(),
  billableMinutes: z.number().int(),
  entryCount: z.number().int(),
  submittedAt: timestampSchema.nullable(),
  approvedByUserId: uuidSchema.nullable(),
  approvedAt: timestampSchema.nullable(),
  note: z.string().nullable(),
  ...timestampsShape,
});
export type TimesheetDto = z.infer<typeof timesheetSchema>;

/** POST /api/v1/time/timesheets/submit (perm `time:track`) → the submitted timesheet. */
export const submitTimesheetInputSchema = z.object({ weekStart: dateOnlySchema, note: z.string().trim().max(1000).nullable().optional() }).strict();
export type SubmitTimesheetInput = z.input<typeof submitTimesheetInputSchema>;

/** POST /api/v1/time/timesheets/:id/approve|reject (perm `time:manage`). Rejecting needs a note. */
export const reviewTimesheetInputSchema = z.object({ note: z.string().trim().max(1000).nullable().optional() }).strict();
export type ReviewTimesheetInput = z.input<typeof reviewTimesheetInputSchema>;

export const listTimesheetsQuerySchema = listQuerySchema.extend({
  userId: uuidSchema.optional(),
  status: timesheetStatusSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});
export type ListTimesheetsQuery = z.infer<typeof listTimesheetsQuerySchema>;

// ---------------------------------------------------------------------------
// The week grid (US-10.2)
// ---------------------------------------------------------------------------

export const weekDaySchema = z.object({
  date: dateOnlySchema,
  minutes: z.number().int(),
  billableMinutes: z.number().int(),
});
export type WeekDayDto = z.infer<typeof weekDaySchema>;

/** GET /api/v1/time/my-week?weekStart= (perm `time:track`) — the fast path: one query for the entries. */
export const weekGridSchema = z.object({
  userId: uuidSchema,
  weekStart: dateOnlySchema,
  weekEnd: dateOnlySchema,
  days: z.array(weekDaySchema),
  entries: z.array(timeEntrySchema),
  totalMinutes: z.number().int(),
  billableMinutes: z.number().int(),
  capacityMinutes: z.number().int(),
  timesheet: timesheetSchema.nullable(),
});
export type WeekGridDto = z.infer<typeof weekGridSchema>;

export const weekQuerySchema = z.object({ weekStart: dateOnlySchema.optional(), userId: uuidSchema.optional() }).strict();
export type WeekQuery = z.infer<typeof weekQuerySchema>;

/** One row of the manager's week view. */
export const teamWeekRowSchema = z.object({
  userId: uuidSchema,
  user: userRefSchema.nullable(),
  days: z.array(weekDaySchema),
  totalMinutes: z.number().int(),
  billableMinutes: z.number().int(),
  capacityMinutes: z.number().int(),
  /** billableMinutes ÷ capacityMinutes, in basis points (US-10.8). */
  utilisationBps: z.number().int(),
  timesheetStatus: timesheetStatusSchema.nullable(),
});
export type TeamWeekRowDto = z.infer<typeof teamWeekRowSchema>;

/** GET /api/v1/time/team?weekStart= (perm `time:manage`). */
export const teamWeekSchema = z.object({
  weekStart: dateOnlySchema,
  weekEnd: dateOnlySchema,
  rows: z.array(teamWeekRowSchema),
  totalMinutes: z.number().int(),
  billableMinutes: z.number().int(),
});
export type TeamWeekDto = z.infer<typeof teamWeekSchema>;

export const teamWeekQuerySchema = z.object({ weekStart: dateOnlySchema.optional(), projectId: uuidSchema.optional() }).strict();
export type TeamWeekQuery = z.infer<typeof teamWeekQuerySchema>;

// ---------------------------------------------------------------------------
// Budgets (US-10.4)
// ---------------------------------------------------------------------------

/** What a budget has consumed so far, and when it runs out at the current pace. */
export const budgetBurnSchema = z.object({
  budgetId: uuidSchema,
  budgetType: budgetTypeSchema,
  /** Minutes for an `hours` budget, minor units otherwise. */
  limitValue: z.number().int().nullable(),
  consumedValue: z.number().int().nullable(),
  /** consumed ÷ limit in basis points; null when the caller may not see money. */
  consumedBps: z.number().int().nullable(),
  minutes: z.number().int(),
  timeCostMinor: minorSchema.nullable(),
  timeBillableMinor: minorSchema.nullable(),
  expenseMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  /** "ok" | "warning" (≥ 80%) | "critical" (≥ 100%). */
  state: z.enum(["ok", "warning", "critical"]),
  /** Straight-line projection of the exhaustion date, or null when nothing has burned yet. */
  forecastExhaustedOn: dateOnlySchema.nullable(),
});
export type BudgetBurnDto = z.infer<typeof budgetBurnSchema>;

export const budgetSchema = z.object({
  id: uuidSchema,
  projectId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  clientId: uuidSchema.nullable(),
  name: z.string(),
  budgetType: budgetTypeSchema,
  amountMinor: minorSchema.nullable(),
  minutes: z.number().int().nullable(),
  currency: currencyCodeSchema,
  warnThresholdBps: z.number().int(),
  criticalThresholdBps: z.number().int(),
  periodStart: dateOnlySchema.nullable(),
  periodEnd: dateOnlySchema.nullable(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  burn: budgetBurnSchema.nullable(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type BudgetDto = z.infer<typeof budgetSchema>;

const budgetWritableShape = {
  projectId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  name: z.string().trim().min(1).max(200),
  budgetType: budgetTypeSchema,
  amountMinor: minorSchema.min(0).nullable(),
  minutes: z.number().int().min(0).nullable(),
  currency: currencyCodeSchema,
  warnThresholdBps: z.number().int().min(1).max(20000),
  criticalThresholdBps: z.number().int().min(1).max(20000),
  periodStart: dateOnlySchema.nullable(),
  periodEnd: dateOnlySchema.nullable(),
  isActive: z.boolean(),
  notes: z.string().trim().max(2000).nullable(),
};

/** POST /api/v1/budgets (perms `financials:view` + `time:manage`) → 201 Budget. Two live budgets on one project → CONFLICT. */
export const createBudgetInputSchema = z
  .object({
    ...budgetWritableShape,
    projectId: budgetWritableShape.projectId.optional(),
    engagementId: budgetWritableShape.engagementId.optional(),
    amountMinor: budgetWritableShape.amountMinor.optional(),
    minutes: budgetWritableShape.minutes.optional(),
    currency: budgetWritableShape.currency.optional(),
    warnThresholdBps: budgetWritableShape.warnThresholdBps.optional(),
    criticalThresholdBps: budgetWritableShape.criticalThresholdBps.optional(),
    periodStart: budgetWritableShape.periodStart.optional(),
    periodEnd: budgetWritableShape.periodEnd.optional(),
    isActive: budgetWritableShape.isActive.optional(),
    notes: budgetWritableShape.notes.optional(),
  })
  .strict();
export type CreateBudgetInput = z.input<typeof createBudgetInputSchema>;

/** PATCH /api/v1/budgets/:id (perms `financials:view` + `time:manage`) → Budget. */
export const updateBudgetInputSchema = z.object(budgetWritableShape).partial().strict();
export type UpdateBudgetInput = z.input<typeof updateBudgetInputSchema>;

export const listBudgetsQuerySchema = listQuerySchema.extend({
  projectId: uuidSchema.optional(),
  engagementId: uuidSchema.optional(),
  clientId: uuidSchema.optional(),
  budgetType: budgetTypeSchema.optional(),
  state: z.enum(["ok", "warning", "critical"]).optional(),
  includeArchived: z.enum(["true", "false"]).optional(),
});
export type ListBudgetsQuery = z.infer<typeof listBudgetsQuerySchema>;

// ---------------------------------------------------------------------------
// The invoicing seam (US-10.7, principles §7.6 seam S2)
// ---------------------------------------------------------------------------

/** One candidate invoice line: a group of unbilled entries or one rebillable expense. */
export const billableGroupSchema = z.object({
  /** Stable key of the group, e.g. "time:task:<uuid>" — what the UI checkboxes key off. */
  key: z.string(),
  source: z.enum(["time", "expense"]),
  clientId: uuidSchema,
  projectId: uuidSchema.nullable(),
  projectName: z.string().nullable(),
  serviceId: uuidSchema.nullable(),
  description: z.string(),
  /** Thousandths of a unit — 1500 = 1.5 hours, matching an invoice line's `quantityMilli`. */
  quantityMilli: z.number().int(),
  unit: z.string(),
  unitPriceMinor: minorSchema,
  amountMinor: minorSchema,
  currency: currencyCodeSchema,
  minutes: z.number().int(),
  /** The rows this group would consume. Sent back verbatim to POST /time/bill. */
  refIds: z.array(uuidSchema),
  earliestOn: dateOnlySchema,
  latestOn: dateOnlySchema,
});
export type BillableGroupDto = z.infer<typeof billableGroupSchema>;

/** GET /api/v1/time/unbilled?clientId= (perms `invoices:read` + `financials:view`). */
export const unbilledSchema = z.object({
  clientId: uuidSchema.nullable(),
  groupBy: z.enum(UNBILLED_GROUPINGS),
  groups: z.array(billableGroupSchema),
  totalMinutes: z.number().int(),
  totalMinor: minorSchema,
  currency: currencyCodeSchema,
});
export type UnbilledDto = z.infer<typeof unbilledSchema>;

export const unbilledQuerySchema = z
  .object({
    clientId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    groupBy: z.enum(UNBILLED_GROUPINGS).default("task"),
    /**
     * Narrow the list to entries a manager has signed off. Off unless asked
     * for: a workspace with `requireApproval` turned off would otherwise never
     * be able to bill anything.
     */
    approvedOnly: queryBooleanSchema.optional(),
    includeExpenses: queryBooleanSchema.optional(),
  })
  .strict();
export type UnbilledQuery = z.infer<typeof unbilledQuerySchema>;

/**
 * POST /api/v1/time/bill (perm `invoices:write`) → what was stamped.
 * One transaction, rows taken `FOR UPDATE`: the same hour can never be billed
 * twice, and an already-billed id answers 409 naming the document.
 */
export const billTimeInputSchema = z
  .object({
    invoiceId: uuidSchema,
    entryIds: z.array(uuidSchema).max(2000).optional(),
    expenseIds: z.array(uuidSchema).max(2000).optional(),
  })
  .strict();
export type BillTimeInput = z.input<typeof billTimeInputSchema>;

export const billResultSchema = z.object({
  invoiceId: uuidSchema,
  timeEntries: z.number().int(),
  expenses: z.number().int(),
  minutes: z.number().int(),
  amountMinor: minorSchema,
  currency: currencyCodeSchema,
});
export type BillResultDto = z.infer<typeof billResultSchema>;

/** POST /api/v1/time/unbill (perm `invoices:write`) — what a credit note or a deleted draft releases. */
export const unbillInputSchema = z.object({ invoiceId: uuidSchema }).strict();
export type UnbillInput = z.input<typeof unbillInputSchema>;

// ---------------------------------------------------------------------------
// Profitability (US-10.6)
// ---------------------------------------------------------------------------

export const profitabilityRowSchema = z.object({
  dimension: z.enum(PROFITABILITY_DIMENSIONS),
  id: uuidSchema.nullable(),
  label: z.string(),
  /** Billed value EXCLUDING GST — the only base for revenue (PRD §7). */
  revenueMinor: minorSchema,
  timeCostMinor: minorSchema,
  expenseMinor: minorSchema,
  freelancerMinor: minorSchema,
  costMinor: minorSchema,
  marginMinor: minorSchema,
  /** margin ÷ revenue in basis points; null when there is no revenue to divide by. */
  marginBps: z.number().int().nullable(),
  minutes: z.number().int(),
  billableMinutes: z.number().int(),
  /** revenue ÷ hours, in minor units per hour; null when no time was logged. */
  effectiveHourlyMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
});
export type ProfitabilityRowDto = z.infer<typeof profitabilityRowSchema>;

/** GET /api/v1/time/profitability (perms `reports:view` + `financials:view`). */
export const profitabilitySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  dimension: z.enum(PROFITABILITY_DIMENSIONS),
  rows: z.array(profitabilityRowSchema),
  totals: profitabilityRowSchema,
});
export type ProfitabilityDto = z.infer<typeof profitabilitySchema>;

export const profitabilityQuerySchema = z
  .object({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    dimension: z.enum(PROFITABILITY_DIMENSIONS).default("client"),
    clientId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type ProfitabilityQuery = z.infer<typeof profitabilityQuerySchema>;

// ---------------------------------------------------------------------------
// Workspace settings
// ---------------------------------------------------------------------------

/** GET/PATCH /api/v1/time/settings (read: any member · write: perm `time:manage`). */
export const timeSettingsSchema = z.object({
  roundingMinutes: z.number().int(),
  defaultBillable: z.boolean(),
  /** ISO weekday the timesheet week starts on (1 = Monday). */
  weekStartsOn: z.number().int(),
  requireApproval: z.boolean(),
  reminderEnabled: z.boolean(),
  ...timestampsShape,
});
export type TimeSettingsDto = z.infer<typeof timeSettingsSchema>;

export const updateTimeSettingsInputSchema = z
  .object({
    roundingMinutes: z.union([z.literal(1), z.literal(5), z.literal(6), z.literal(15)]),
    defaultBillable: z.boolean(),
    weekStartsOn: z.number().int().min(1).max(7),
    requireApproval: z.boolean(),
    reminderEnabled: z.boolean(),
  })
  .partial()
  .strict();
export type UpdateTimeSettingsInput = z.input<typeof updateTimeSettingsInputSchema>;
